import type { ReactNode } from 'react';
import { boundedTone, level, type Tone } from './state.ts';
import { Unit } from './Unit.tsx';
import { Sparkline } from './Sparkline.tsx';
import styles from './MetricCard.module.css';

/* ==========================================================================
   MetricCard — one figure in a KPI strip. Deliberately not a card.

   A hero card with a shadow and 24px of padding is the fastest way to turn a
   console into a dashboard. This is a 40px cell in a hairline-divided strip.
   Where a fraction is the truth, show the fraction: `12/28 Active`, not 43%.

   The figure is `--text-hi` and stays that way. Give it `warn`/`bad` and it
   colours the same way every other bounded reading in the console does —
   only once a limit is crossed — so a KPI strip on a healthy fleet is a row
   of white numbers, and the one number that has gone amber is findable from
   across the room. A literal `tone` is honoured only inside those limits.
   ========================================================================== */

export function MetricCard({
  eyebrow,
  value,
  unit,
  /** Denominator, when the honest form is a fraction. */
  of,
  note,
  tone,
  /** The figure as a number, when `value` is pre-formatted. Enables warn/bad. */
  reading,
  warn,
  bad,
  invert = false,
  /** Recent history for this figure, if the API actually provides it. */
  trend,
  /** Epoch ms / ISO stamps aligned with `trend`, so the crosshair can answer
   *  "what was it at 22:41Z" rather than only "it moved". */
  trendTimes,
}: {
  eyebrow: string;
  value: ReactNode;
  unit?: string | undefined;
  of?: ReactNode;
  note?: ReactNode;
  tone?: Tone | undefined;
  reading?: number | null | undefined;
  warn?: number | undefined;
  bad?: number | undefined;
  invert?: boolean | undefined;
  trend?: readonly (number | null | undefined)[] | undefined;
  trendTimes?: readonly (number | string | null | undefined)[] | undefined;
}) {
  const bounded = warn !== undefined || bad !== undefined;
  const lv = bounded
    ? level(reading, warn ?? bad ?? 0, bad ?? warn ?? 0, invert)
    : ('ok' as const);
  const resolved = bounded ? boundedTone(lv, tone) : tone;
  /* Colour the figure only when a limit is crossed, or when the caller has
     deliberately named a tone for an unbounded fact. */
  const toned = bounded ? lv === 'warn' || lv === 'bad' : tone !== undefined;

  return (
    <div className={styles.stat} data-tone={resolved} data-toned={toned}>
      <span className={styles.eyebrow}>{eyebrow}</span>
      <span className={styles.valueRow}>
        <span className={styles.value}>{value}</span>
        {of !== undefined && <span className={styles.of}>/{of}</span>}
        {unit !== undefined && (
          <Unit unit={unit} className={styles.unit} />
        )}
        {trend !== undefined && (
          <span className={styles.spark}>
            <Sparkline
              values={trend}
              times={trendTimes}
              tone={resolved ?? 'nominal'}
              warn={warn}
              bad={bad}
              invert={invert}
              unit={unit}
              label={eyebrow}
              width={40}
              height={14}
            />
          </span>
        )}
      </span>
      {note !== undefined && <span className={styles.note}>{note}</span>}
    </div>
  );
}

/** The hairline-divided band that MetricCards sit in. */
export function MetricStrip({ children }: { children: ReactNode }) {
  return <div className={styles.strip}>{children}</div>;
}
